// Weapon viewmodel + hitscan shooting logic.
// - Reads the active weapon from matchStore, rendering the right model.
// - Handles fire, reload, and switch animations off the same store.
// - All weapon stats (damage, cooldown, range, model) come from
//   /game/weapons/weapons.js — modifying a weapon is a one-file change.
import { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useMatchStore } from "@/stores/matchStore";
import { getWeapon } from "@/game/weapons/weapons";
import { getAgent } from "@/game/agents/agents";
import { useSettings } from "@/stores/settingsStore";
import { Audio_ } from "@/lib/audio";
import { raycastWalls } from "@/game/systems/collision";

export default function Weapon({ playerRef, bots, walls, onBotHit }) {
    const groupRef = useRef();
    const muzzleFlashRef = useRef();
    const { camera } = useThree();
    const state = useRef({
        lastFire: 0,
        reloadEndsAt: 0,
        muzzleUntil: 0,
        activeWeaponId: null,
        switchStartedAt: 0,
        // ADS blend 0 (hip) → 1 (aimed)
        adsTarget: 0,
        adsCurrent: 0,
        baseFov: null,
    });

    // Track weapon changes so we can trigger switch animation + cancel reload
    useEffect(() => {
        const unsub = useMatchStore.subscribe((s) => {
            if (state.current.activeWeaponId !== s.activeWeapon) {
                state.current.activeWeaponId = s.activeWeapon;
                state.current.switchStartedAt = performance.now();
                state.current.reloadEndsAt = 0;
                Audio_.play("weapon_switch");
            }
            // If a reload just started via any code path, note when it will finish
            const active = s.inventory?.[s.activeWeapon];
            if (active?.reloading && state.current.reloadEndsAt === 0) {
                const w = getWeapon(s.activeWeapon);
                const agentId = useSettings.getState().selectedAgent || "recruit";
                const reloadMult = getAgent(agentId).passive?.reloadSpeedMult ?? 1;
                state.current.reloadEndsAt = performance.now() + w.reloadMs / reloadMult;
            }
        });
        return unsub;
    }, []);

    // Mouse fire + right-click ADS
    useEffect(() => {
        const onDown = (e) => {
            if (e.button === 0) {
                fire();
            } else if (e.button === 2) {
                state.current.adsTarget = 1;
            }
        };
        const onUp = (e) => {
            if (e.button === 2) state.current.adsTarget = 0;
        };
        const onCtx = (e) => e.preventDefault();
        window.addEventListener("mousedown", onDown);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("contextmenu", onCtx);
        return () => {
            window.removeEventListener("mousedown", onDown);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("contextmenu", onCtx);
        };
    }, []);

    function fire() {
        const now = performance.now();
        const match = useMatchStore.getState();
        if (match.phase !== "playing") return;
        const active = match.activeWeapon;
        const w = getWeapon(active);
        if (now - state.current.lastFire < w.fireCooldownMs) return;
        // Block firing during switch animation
        if (now - state.current.switchStartedAt < 200) return;

        const fired = match.fireShot();
        if (!fired) {
            if ((match.inventory[active]?.ammo ?? 0) <= 0) {
                match.startReload();
                Audio_.play(w.sfx.reload || "reload", { volume: 0.55 });
            }
            return;
        }
        state.current.lastFire = now;
        state.current.muzzleUntil = now + 60;
        playerRef.current?.applyRecoil(w.id === "smg" ? -0.005 : w.id === "pistol" ? -0.012 : -0.008);
        Audio_.play(w.sfx.shoot || "shoot", { volume: 0.55 });

        // Hitscan
        const origin = playerRef.current?.getEyePosition() || camera.position.clone();
        const dir = new THREE.Vector3();
        camera.getWorldDirection(dir);

        const wallHit = raycastWalls(origin, dir, walls, w.range);
        const wallT = wallHit ? wallHit.t : w.range;

        let hitBot = null;
        let hitT = wallT;
        for (const b of bots) {
            if (!b.alive) continue;
            const center = new THREE.Vector3(b.position[0], b.position[1] + 1.1, b.position[2]);
            const t = raySphere(origin, dir, center, 0.6);
            if (t !== null && t < hitT) {
                hitT = t;
                hitBot = b;
            }
        }

        if (hitBot) {
            // Head sphere check: upper portion of the bot capsule (~y+1.55)
            const headCenter = new THREE.Vector3(
                hitBot.position[0],
                hitBot.position[1] + 1.55,
                hitBot.position[2]
            );
            const headT = raySphere(origin, dir, headCenter, 0.28);
            const isHead = headT !== null && Math.abs(headT - hitT) < 0.4;

            const agentId = useSettings.getState().selectedAgent || "recruit";
            const passive = getAgent(agentId).passive || {};
            const dmgMult = passive.damageMult ?? 1;
            const hsBonus = isHead ? (passive.headshotBonus ?? 1.5) : 1;
            const finalDamage = Math.round(w.damage * dmgMult * hsBonus);
            onBotHit?.(hitBot.id, finalDamage, { headshot: isHead });
            Audio_.play(isHead ? "hit_head" : "hit", { volume: isHead ? 0.75 : 0.6 });
        }
    }

    useFrame((_, dt) => {
        const now = performance.now();
        // Finish reload
        if (state.current.reloadEndsAt > 0 && now >= state.current.reloadEndsAt) {
            useMatchStore.getState().finishReload();
            state.current.reloadEndsAt = 0;
        }

        if (!groupRef.current) return;

        const match = useMatchStore.getState();
        const w = getWeapon(match.activeWeapon);

        // ADS FOV blend — Marksman gets slower ADS via adsSpeedMult
        const agentId = useSettings.getState().selectedAgent || "recruit";
        const passive = getAgent(agentId).passive || {};
        const adsSpeedMult = passive.adsSpeedMult ?? 1;
        const settings = useSettings.getState();
        if (state.current.baseFov == null) state.current.baseFov = settings.fov;
        // Keep base fov in sync with settings changes
        state.current.baseFov = settings.fov;
        // Blend rate ~ 10 units per sec, scaled by adsSpeedMult (< 1 = slower)
        const blendRate = Math.min(1, dt * 10 * adsSpeedMult);
        state.current.adsCurrent = THREE.MathUtils.lerp(
            state.current.adsCurrent,
            state.current.adsTarget,
            blendRate
        );
        const targetFov = state.current.baseFov * (1 - state.current.adsCurrent * 0.4);
        if (Math.abs(camera.fov - targetFov) > 0.01) {
            camera.fov = targetFov;
            camera.updateProjectionMatrix();
        }

        const cam = camera;
        const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
        const right = new THREE.Vector3(1, 0, 0).applyQuaternion(cam.quaternion);
        const up = new THREE.Vector3(0, 1, 0).applyQuaternion(cam.quaternion);

        // ADS pulls the weapon toward the sight line
        const ads = state.current.adsCurrent;
        const [ox, oy, oz] = w.model.offset;
        const adsOx = THREE.MathUtils.lerp(ox, 0.0, ads);
        const adsOy = THREE.MathUtils.lerp(oy, -0.08, ads);
        const adsOz = THREE.MathUtils.lerp(oz, oz - 0.15, ads);
        const offset = new THREE.Vector3()
            .addScaledVector(right, adsOx)
            .addScaledVector(up, adsOy)
            .addScaledVector(forward, adsOz);

        // Reload dip
        let reloadDip = 0;
        if (state.current.reloadEndsAt > 0) {
            const p = 1 - (state.current.reloadEndsAt - now) / w.reloadMs;
            reloadDip = Math.sin(p * Math.PI) * 0.35;
        }
        offset.addScaledVector(up, -reloadDip);

        // Weapon switch dip
        const sinceSwitch = now - state.current.switchStartedAt;
        let switchDip = 0;
        if (sinceSwitch < 260) {
            const p = sinceSwitch / 260;
            switchDip = Math.sin(p * Math.PI) * 0.45;
        }
        offset.addScaledVector(up, -switchDip);

        groupRef.current.position.copy(cam.position).add(offset);
        groupRef.current.quaternion.copy(cam.quaternion);
        groupRef.current.rotation.x += reloadDip * 0.6 + switchDip * 0.6;

        if (muzzleFlashRef.current) {
            muzzleFlashRef.current.visible = now < state.current.muzzleUntil;
        }
    });

    // Read current weapon (React state so mesh re-renders on switch)
    const activeId = useMatchStore((s) => s.activeWeapon);
    const w = getWeapon(activeId);

    return (
        <group ref={groupRef} key={activeId}>
            {/* Rifle / SMG / Pistol geometry driven by weapon config */}
            <mesh position={[0, 0, -0.25]}>
                <boxGeometry args={w.model.bodySize} />
                <meshStandardMaterial color={w.model.color} metalness={0.7} roughness={0.4} />
            </mesh>
            {/* Barrel */}
            <mesh position={[0, 0.02, -(0.25 + w.model.bodySize[2] / 2 + w.model.barrelLen / 2)]}>
                <cylinderGeometry args={[0.02, 0.02, w.model.barrelLen, 8]} />
                <meshStandardMaterial color={w.model.barrelColor} metalness={0.9} roughness={0.3} />
            </mesh>
            {/* Grip */}
            <mesh position={[0, -0.09, -0.14]} rotation={[0.2, 0, 0]}>
                <boxGeometry args={[0.05, 0.16, 0.08]} />
                <meshStandardMaterial color={w.model.gripColor} metalness={0.4} roughness={0.7} />
            </mesh>
            {/* Sight */}
            <mesh position={[0, 0.08, -0.28]}>
                <boxGeometry args={[0.04, 0.06, 0.12]} />
                <meshStandardMaterial
                    color={w.model.sightColor}
                    emissive={w.model.sightColor}
                    emissiveIntensity={0.4}
                    metalness={0.6}
                    roughness={0.3}
                />
            </mesh>
            {/* Magazine */}
            <mesh position={[0, -0.13, -0.24]}>
                <boxGeometry args={w.model.magSize} />
                <meshStandardMaterial color="#111820" metalness={0.5} roughness={0.5} />
            </mesh>

            {/* Muzzle flash */}
            <group
                ref={muzzleFlashRef}
                position={[0, 0.02, -(0.25 + w.model.bodySize[2] / 2 + w.model.barrelLen)]}
                visible={false}
            >
                <mesh>
                    <sphereGeometry args={[0.09, 8, 8]} />
                    <meshBasicMaterial color="#ffe082" transparent opacity={0.9} />
                </mesh>
                <pointLight color="#ffb300" intensity={2} distance={5} />
            </group>
        </group>
    );
}

function raySphere(origin, dir, center, radius) {
    const oc = new THREE.Vector3().subVectors(origin, center);
    const b = oc.dot(dir);
    const c = oc.dot(oc) - radius * radius;
    const disc = b * b - c;
    if (disc < 0) return null;
    const t = -b - Math.sqrt(disc);
    return t > 0 ? t : null;
}
